import { useFormsQuery } from "@shared/data/queries";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch";

const useProductSearchVariables = () => {
  const {
    activeIngredients,
    excludedIngredients,
    activeBrands,
    activeSupplementTypes,
    activeConditions,
    activeFilters,
    activeCategories,
    searchQuery,
    sortDirection,
    sortField,
    isOnlyShowFavorites,
    onlyIngredient,
    isOnlyShowInStock,
    typeaheadSelectionId,
  } = useCatalogSearch();

  const { data: formsData } = useFormsQuery();

  const getActiveFilterIds = (category: string) =>
    activeFilters
      .filter(filter => filter.category === category)
      .map(filteredFilter => filteredFilter.id);

  const brandIds = activeBrands.map(brand => brand.id);

  const supplementTypeIds = activeSupplementTypes.map(type => type.id);

  const conditionIds = activeConditions?.map(condition => condition.id);

  const categoryIds = activeCategories?.map(category => category.id);

  const ingredients = activeIngredients
    ? activeIngredients.map(ingredient => ingredient.searchAttributes)
    : null;

  const excludedIngredientsForQuery = excludedIngredients
    ? excludedIngredients.map(ingredient => ingredient.searchAttributes)
    : null;

  const allergenIds = getActiveFilterIds("Allergens");
  const thirdPartyCertificationIds = getActiveFilterIds("Certifications");
  const populationIds = getActiveFilterIds("Demographic");
  const baseDosageFormatNames = activeFilters
    .filter(filter => filter.category === "Form")
    .map(filteredFilter => filteredFilter.name);

  const dosageFormatIds: string[] =
    formsData?.viewer?.forms
      ?.filter(form => baseDosageFormatNames.includes(form.baseFormat))
      .map(filteredForm => filteredForm.id) || [];

  const onlyWholesaleHistory =
    getActiveFilterIds("More")?.filter(id => id === "previouslyOrdered")?.length > 0;

  const showOnlySaleItems = getActiveFilterIds("More")?.filter(id => id === "onSale")?.length > 0;

  const sort = sortDirection && sortField ? [{ direction: sortDirection, field: sortField }] : [];

  return {
    query: searchQuery,
    typeaheadSelectionId,
    sort,
    filters: {
      onlyShowFavorites: isOnlyShowFavorites,
      wholesaleDiscounted: showOnlySaleItems,
      brandIds,
      supplementTypeIds,
      conditionIds,
      categoryIds,
      ingredients,
      excludedIngredients: excludedIngredientsForQuery,
      allergenIds,
      thirdPartyCertificationIds,
      populationIds,
      dosageFormatIds,
      inStockOnly: isOnlyShowInStock,
      onlyIngredient,
      onlyWholesaleHistory,
    },
  };
};

export { useProductSearchVariables };
